import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import React, {createRef, ReactNode, RefObject} from "react";
import Translate from "../../../../shared/translation/Translate";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import Helper from "../../../../shared/components/Helper";
import SignatureAppearanceDialogFields from "../../field/signature/SignatureAppearanceDialogFields";
import PortalContext from "../../../PortalContext";
import {
	AppearanceAdd,
	AppearanceAddInterface,
	SignatureImage,
	SignatureImagePosition
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import {TabPanel, TabView} from "primereact/tabview";
import {InputText} from "primereact/inputtext";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import ReactSignatureCanvas from "react-signature-canvas";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export interface SignatureAppearanceDialogProperties extends AbstractDialogProperties {
	context: PortalContext;
	parameter?: AppearanceAddInterface;
	imageFile: File;
	textSignature: string;
	submit: (parameter: AppearanceAdd, imageFile: File) => void;
}

export interface SignatureAppearanceDialogState extends AbstractDialogState {
	parameter?: AppearanceAdd;
	typeFile: File;
	signFile: File;
	imageFile: File;
	panel: number;
	textSignature: string;
}

enum SignatureType {
	TYPE,
	SIGN,
	IMAGE
}

export class SignatureAppearanceDialogDefinition extends AbstractDialog<SignatureAppearanceDialogProperties,
	SignatureAppearanceDialogState> {
	private signaturePad: RefObject<ReactSignatureCanvas>;

	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<SignatureAppearanceDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			panel: SignatureType.SIGN,
			textSignature: "",
			typeFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			signFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			imageFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		}

		this.hide = this.hide.bind(this);
		this.submit = this.submit.bind(this);
		this.onChange = this.onChange.bind(this);
		this.renderError = this.renderError.bind(this);

		this.signaturePad = createRef<ReactSignatureCanvas>();
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		let resultFile: File = this.state.imageFile;

		switch (this.state.panel) {
			case SignatureType.SIGN:
				resultFile = this.state.signFile;
				break;
			case SignatureType.TYPE:
				resultFile = this.state.typeFile;
				break;
		}

		this.props.submit(this.state.parameter!, resultFile);
		this.hide();
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-signature small"}/>
					<Translate {...this.props} data="signature.appearance.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.select")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted && !FunctionHelper.exists(this.state.exception);
	}

	private renderPreview(type: SignatureType): ReactNode {
		let parameter: AppearanceAdd = this.state.parameter!;

		let textAlign: any = "left";
		let justifyContent: string = "flex-start"
		switch (FunctionHelper.applyValue(parameter.image?.position, SignatureImage.getPositionDefault())) {
			case SignatureImagePosition.Center:
				justifyContent = "center";
				textAlign = "center";
				break;
			case SignatureImagePosition.Right:
				justifyContent = "flex-end";
				textAlign = "right";
				break;
		}

		return (
			<div className="signature-preview"
				 style={{
					 justifyContent: justifyContent
				 }}>
				{type === SignatureType.IMAGE &&
					<>
						<FileInput inputId="control-file" value={this.state.imageFile.value}
								   filename={this.state.imageFile.name}
								   accept="image/gif, image/png, image/jpeg"
								   onChange={(files) => this.onChange(
									   SignatureAppearanceDialogFields.FILE, files[0]
								   )}/>
						{this.state.imageFile.value !== "" &&
							<img alt={Translate.value("signature.appearance.alt", {ns: "dialog"})}
								 src={"data:" + this.state.imageFile.mimetype + ";base64," + this.state.imageFile.value}
								 style={{
									 opacity: FunctionHelper.applyValue(
										 parameter.image?.opacity, SignatureImage.getOpacityDefault()
									 ) / 100
								 }}/>
						}
					</>
				}
				{type === SignatureType.SIGN &&
					<ReactSignatureCanvas ref={this.signaturePad} clearOnResize={false}
										  onEnd={() => {
											  this.onChange(
												  SignatureAppearanceDialogFields.SIGNATURE,
												  this.signaturePad.current?.getTrimmedCanvas().toDataURL(
													  MimeTypes.PNG.toString()
												  ).split(',')[1]
											  );
										  }}/>
				}
				{type === SignatureType.TYPE &&
					<InputText value={this.state.textSignature} id="control-text"
							   style={{
								   fontSize: "30px",
								   fontFamily: "Caveat",
								   textAlign: textAlign
							   }}
							   onInput={(e) => this.onChange(
								   SignatureAppearanceDialogFields.TEXT, e.currentTarget.value
							   )}/>
				}
				<Button className="clear" icon="icon icon-delete small"
						tooltip={Translate.value("signature.appearance.clear", {ns: "dialog"})}
						onClick={() => {
							switch (type) {
								case SignatureType.IMAGE:
									this.setState({
										imageFile: {
											name: "",
											mimetype: "",
											value: ""
										}
									});
									break;
								case SignatureType.TYPE:
									this.setState({
										textSignature: "",
										typeFile: {
											name: "",
											mimetype: "",
											value: ""
										}
									});
									break;
								case SignatureType.SIGN:
									this.signaturePad.current?.clear();
									this.setState({
										signFile: {
											name: "",
											mimetype: "",
											value: ""
										}
									});
									break;
							}
						}}/>
			</div>
		);
	}

	private renderContent(): ReactNode {
		let parameter: AppearanceAdd | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<div className="p-col-12 p-grid">
					<TabView activeIndex={this.state.panel}
							 onTabChange={(e) => {
								 switch (e.index) {
									 case SignatureType.TYPE:
										 this.onChange(SignatureAppearanceDialogFields.TEXT, this.state.textSignature);
										 break;
								 }

								 this.setState({
									 panel: e.index
								 });
							 }}>
						<TabPanel header={Translate.value("signature.appearance.type", {ns: "dialog"})}>
							{this.renderPreview(SignatureType.TYPE)}
						</TabPanel>
						<TabPanel header={Translate.value("signature.appearance.sign", {ns: "dialog"})}>
							{this.renderPreview(SignatureType.SIGN)}
						</TabPanel>
						<TabPanel header={Translate.value("signature.appearance.image", {ns: "dialog"})}>
							{this.renderPreview(SignatureType.IMAGE)}
							{/*<div className="p-col-12 p-lg-6 p-grid" style={{display: "none"}}>*/}
							{/*	<label className="p-col-12 p-sm-5" htmlFor="control-file">*/}
							{/*		<Translate {...this.props} data="signature.appearance.file" namespace="dialog"/>*/}
							{/*	</label>*/}
							{/*	<div className="p-col-12 p-sm-7">*/}
							{/*		<FileInput inputId="control-file" value={this.state.imageFile.value}*/}
							{/*				   filename={this.state.imageFile.name}*/}
							{/*				   accept="image/gif, image/png, image/jpeg"*/}
							{/*				   onChange={(files) => this.onChange(*/}
							{/*					   SignatureAppearanceDialogFields.FILE, files[0]*/}
							{/*				   )}/>*/}
							{/*	</div>*/}
							{/*</div>*/}
						</TabPanel>
					</TabView>

					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-opacity">
								<Translate {...this.props} data="signature.appearance.opacity" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<InputNumber inputId="control-opacity" step={1} showButtons={true} suffix=" %"
											 useGrouping={false}
											 value={FunctionHelper.applyValue(
												 parameter?.image?.opacity, SignatureImage.getOpacityDefault()
											 )}
											 min={SignatureImage.getOpacityMin()} max={SignatureImage.getOpacityMax()}
											 onChange={(e) => this.onChange(
												 SignatureAppearanceDialogFields.OPACITY, e.value
											 )}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-position">
								<Translate {...this.props} data="signature.appearance.position.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-position"
										  value={FunctionHelper.applyValue(
											  parameter?.image?.position, SignatureImage.getPositionDefault()
										  )}
										  onChange={(e) => this.onChange(
											  SignatureAppearanceDialogFields.POSITION, e.value
										  )}
										  appendTo={document.getElementById("root")}
										  options={[
											  {
												  label: Translate.value(
													  "signature.appearance.position.center",
													  {ns: "dialog"}
												  ),
												  value: SignatureImagePosition.Center
											  },
											  {
												  label: Translate.value(
													  "signature.appearance.position.left",
													  {ns: "dialog"}
												  ),
												  value: SignatureImagePosition.Left
											  },
											  {
												  label: Translate.value(
													  "signature.appearance.position.right",
													  {ns: "dialog"}
												  ),
												  value: SignatureImagePosition.Right
											  }
										  ]}/>
							</div>
						</div>
					</fieldset>
				</div>
			</>
		)
	}

	protected renderDialog(): ReactNode {
		let classList: Array<string> = ["signature-appearance-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.signature.appearance"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContent()}
				</div>
			</Dialog>
		);
	}

	private onChange(field: SignatureAppearanceDialogFields, value: any): void {
		let parameter: AppearanceAdd = this.state.parameter!;

		switch (field) {
			case SignatureAppearanceDialogFields.SIGNATURE:
				this.setState({
					signFile: {
						name: "signature",
						mimetype: MimeTypes.PNG.toString(),
						value: value
					}
				});
				break;
			case SignatureAppearanceDialogFields.TEXT:
				if (value.trim() === "") {
					this.setState({
						textSignature: "",
						typeFile: {
							name: "",
							mimetype: "",
							value: ""
						}
					});
					break;
				}

				let canvas: HTMLCanvasElement = document.createElement("canvas");
				let context: CanvasRenderingContext2D | null = canvas.getContext("2d");
				if (FunctionHelper.exists(context)) {
					let font: string = "30px Caveat";
					context!.font = font;
					let textMetrics: TextMetrics = context!.measureText(value);

					canvas.width = textMetrics.width + 5;
					canvas.height = 40;
					context!.font = font;
					context!.fillText(value, 0, 30);

					this.setState({
						typeFile: {
							name: "signature",
							mimetype: MimeTypes.PNG.toString(),
							value: canvas.toDataURL(MimeTypes.PNG.toString()).split(',')[1]
						}
					});
				}

				this.setState({
					textSignature: value
				});
				break;
			case SignatureAppearanceDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				this.setState({
					imageFile: value
				});
				return;
			case SignatureAppearanceDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SignatureImage.getOpacityDefault();
				}

				if (!FunctionHelper.exists(parameter.image)) {
					parameter.image = new SignatureImage({});
				}

				parameter.image!.opacity = value;
				break;
			case SignatureAppearanceDialogFields.POSITION:
				parameter.image!.position = value;
				break;
		}

		this.setState({
			parameter: parameter
		});
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		this.setState({
			textSignature: this.props.textSignature,
			signFile: this.props.imageFile,
			typeFile: this.props.imageFile,
			imageFile: this.props.imageFile,
			panel: SignatureType.SIGN,
			parameter: AppearanceAdd.fromJson(this.props.parameter)
		});
	};
}

const SignatureAppearanceDialog = React.memo(SignatureAppearanceDialogDefinition,
	(
		prevProps: Readonly<SignatureAppearanceDialogProperties>,
		nextProps: Readonly<SignatureAppearanceDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SignatureAppearanceDialog;